import React, { useState, useEffect } from 'react';
import '../styles/QuickAddActivityModal.css';

const ALL_ACTIVITIES = [
  'Strength Training',
  'Run',
  'Walk',
  'Meditation',
  'Yoga',
  'Pilates',
  'Bike',
  'Mountain Bike',
  'Road Bike',
  'Gravel Bike',
  'Trail Run',
  'Track Workout',
  'HIIT',
  'Pool Swim'
];

// Activities that show distance in miles/km
const DISTANCE_ACTIVITIES = ['Run', 'Walk', 'Bike', 'Mountain Bike', 'Road Bike', 'Gravel Bike', 'Trail Run', 'Track Workout'];

// Activities that are duration-only (no distance)
const DURATION_ONLY_ACTIVITIES = ['Strength Training', 'HIIT', 'Yoga', 'Pilates', 'Meditation'];

// Swimming activities - use laps instead of distance
const SWIM_ACTIVITIES = ['Pool Swim'];

// Pool length options
const POOL_LENGTHS = [
  { value: '25yd', label: '25 yd' },
  { value: '25m', label: '25 m' },
  { value: '50m', label: '50 m' }
];

function QuickAddActivityModal({ 
  isOpen, 
  onClose, 
  onSave,
  onEdit, // Called when editing an existing activity
  onDelete, // Called when deleting an activity (edit mode only)
  date,
  distanceUnit = 'miles', // 'miles' or 'km'
  editActivity = null // If provided, modal opens in edit mode
}) {
  // Step 1: Pick activity, Step 2: Enter details
  const [step, setStep] = useState(1);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [customActivity, setCustomActivity] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  
  // Activity details - duration as hours, minutes, and seconds
  const [durationHours, setDurationHours] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [durationSeconds, setDurationSeconds] = useState('');
  const [distance, setDistance] = useState('');
  const [timeHour, setTimeHour] = useState('');
  const [timeMinute, setTimeMinute] = useState('');
  const [timePeriod, setTimePeriod] = useState('AM');
  
  // Swimming-specific
  const [laps, setLaps] = useState('');
  const [poolLength, setPoolLength] = useState('25yd');
  
  // Helper to check if activity matches any in a list (handles combined names like "Run/Walk")
  const activityMatches = (activityName, activityList) => {
    if (!activityName) return false;
    const nameLower = activityName.toLowerCase();
    return activityList.some(item => {
      const itemLower = item.toLowerCase();
      // Exact match or contains match (for combined names like "Run/Walk")
      return nameLower === itemLower || nameLower.includes(itemLower);
    });
  };

  // Determine activity type for showing appropriate fields
  const isSwimActivity = activityMatches(selectedActivity, SWIM_ACTIVITIES);
  const isDistanceActivity = activityMatches(selectedActivity, DISTANCE_ACTIVITIES);
  const isDurationOnlyActivity = activityMatches(selectedActivity, DURATION_ONLY_ACTIVITIES);

  // Check if we're in edit mode
  const isEditMode = editActivity !== null;

  // Parse existing time from activity (24-hour to 12-hour)
  const parseExistingTime = (timeStr) => {
    if (!timeStr) return { hour: '', minute: '', period: 'AM' };
    
    // Handle 24-hour format (HH:mm)
    const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
      let hour = parseInt(match24[1], 10);
      const minute = match24[2];
      const period = hour >= 12 ? 'PM' : 'AM';
      
      if (hour === 0) hour = 12;
      else if (hour > 12) hour = hour - 12;
      
      return { hour: String(hour), minute, period };
    }
    
    // Handle 12-hour format (H:MM AM/PM)
    const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
      return { hour: match12[1], minute: match12[2], period: match12[3].toUpperCase() };
    }
    
    return { hour: '', minute: '', period: 'AM' };
  };

  // Parse duration in seconds to hours, minutes, and seconds
  const parseDurationSeconds = (totalSeconds) => {
    if (!totalSeconds && totalSeconds !== 0) return { hours: '', minutes: '', seconds: '' };
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { 
      hours: hours > 0 ? String(hours) : '', 
      minutes: minutes > 0 ? String(minutes) : (hours > 0 ? '0' : ''),
      seconds: seconds > 0 ? String(seconds) : ''
    };
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Simple overflow hidden - don't change position to avoid breaking fixed elements
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.dataset.scrollY = scrollY;
    }
    
    return () => {
      document.body.style.overflow = '';
      // Restore scroll position if needed
      const scrollY = document.body.dataset.scrollY;
      if (scrollY) {
        delete document.body.dataset.scrollY;
      }
    };
  }, [isOpen]);

  // Reset/initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editActivity) {
        // Edit mode: skip to step 2 and pre-populate fields
        setStep(2);
        setSelectedActivity(editActivity.name || editActivity.activityName || '');
        
        // Parse duration into hours, minutes, and seconds
        // Check if we have seconds stored, otherwise convert from minutes
        const existingDurationSeconds = editActivity.estimatedDurationSeconds || 
          (editActivity.estimatedDuration ? editActivity.estimatedDuration * 60 : null) ||
          (editActivity.duration ? editActivity.duration * 60 : null);
        const parsedDuration = parseDurationSeconds(existingDurationSeconds);
        setDurationHours(parsedDuration.hours);
        setDurationMinutes(parsedDuration.minutes);
        setDurationSeconds(parsedDuration.seconds);
        
        setDistance(editActivity.estimatedDistance ? String(editActivity.estimatedDistance) : '');
        
        // Swimming-specific fields
        setLaps(editActivity.laps ? String(editActivity.laps) : '');
        setPoolLength(editActivity.poolLength || '25yd');
        
        const parsedTime = parseExistingTime(editActivity.scheduledTime);
        setTimeHour(parsedTime.hour);
        setTimeMinute(parsedTime.minute);
        setTimePeriod(parsedTime.period);
        
        setCustomActivity('');
        setIsAddingCustom(false);
      } else {
        // Add mode: start fresh at step 1
        setStep(1);
        setSelectedActivity('');
        setCustomActivity('');
        setIsAddingCustom(false);
        setDurationHours('');
        setDurationMinutes('');
        setDurationSeconds('');
        setDistance('');
        setLaps('');
        setPoolLength('25yd');
        setTimeHour('');
        setTimeMinute('');
        setTimePeriod('AM');
      }
    }
  }, [isOpen, editActivity]);

  if (!isOpen) return null;

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setStep(2);
  };

  const handleCustomActivitySubmit = () => {
    if (customActivity.trim()) {
      setSelectedActivity(customActivity.trim());
      setIsAddingCustom(false);
      setStep(2);
    }
  };

  const handleCustomActivityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCustomActivitySubmit();
    } else if (e.key === 'Escape') {
      setIsAddingCustom(false);
      setCustomActivity('');
    }
  };

  const formatTime = () => {
    if (!timeHour) return null;
    const hour = parseInt(timeHour, 10);
    const minute = timeMinute ? parseInt(timeMinute, 10) : 0;
    
    if (isNaN(hour) || hour < 1 || hour > 12) return null;
    if (isNaN(minute) || minute < 0 || minute > 59) return null;
    
    // Convert to 24-hour format for storage
    let hour24 = hour;
    if (timePeriod === 'PM' && hour !== 12) {
      hour24 = hour + 12;
    } else if (timePeriod === 'AM' && hour === 12) {
      hour24 = 0;
    }
    
    return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  };

  // Calculate total duration in seconds from hours + minutes + seconds inputs
  const calculateDurationSeconds = () => {
    const hours = durationHours ? parseInt(durationHours, 10) : 0;
    const minutes = durationMinutes ? parseInt(durationMinutes, 10) : 0;
    const seconds = durationSeconds ? parseInt(durationSeconds, 10) : 0;
    
    if (isNaN(hours) && isNaN(minutes) && isNaN(seconds)) return null;
    
    const totalSeconds = (isNaN(hours) ? 0 : hours * 3600) + (isNaN(minutes) ? 0 : minutes * 60) + (isNaN(seconds) ? 0 : seconds);
    return totalSeconds > 0 ? totalSeconds : null;
  };

  // Convert seconds to minutes for backward compatibility
  const calculateDurationMinutes = () => {
    const totalSeconds = calculateDurationSeconds();
    return totalSeconds ? Math.round(totalSeconds / 60) : null;
  };

  // Calculate swimming distance from laps and pool length
  // Returns distance in the pool's unit (yards or meters)
  const calculateSwimDistance = () => {
    if (!laps) return null;
    const lapCount = parseInt(laps, 10);
    if (isNaN(lapCount) || lapCount <= 0) return null;
    
    // Parse pool length value and unit
    const poolLengthValue = parseInt(poolLength, 10); // 25 or 50
    const poolLengthUnit = poolLength.includes('yd') ? 'yards' : 'meters';
    
    // Total distance = laps × pool length
    const totalDistance = lapCount * poolLengthValue;
    
    return { distance: totalDistance, unit: poolLengthUnit };
  };

  const handleSave = () => {
    const totalDurationMinutes = calculateDurationMinutes();
    const totalDurationSeconds = calculateDurationSeconds();
    
    if (isEditMode && onEdit) {
      // Edit mode: call onEdit with activity ID and updates
      const updates = {
        estimatedDuration: totalDurationMinutes,
        estimatedDurationSeconds: totalDurationSeconds,
        scheduledTime: formatTime(),
      };
      
      // Add sport-specific fields
      if (isSwimActivity) {
        const swimDistance = calculateSwimDistance();
        updates.laps = laps ? parseInt(laps, 10) : null;
        updates.poolLength = poolLength;
        // Also store calculated distance for Apple Health compatibility
        updates.estimatedDistance = swimDistance?.distance || null;
        updates.distanceUnit = swimDistance?.unit || null;
      } else if (isDistanceActivity) {
        updates.estimatedDistance = distance ? parseFloat(distance) : null;
        updates.distanceUnit = distance ? distanceUnit : null;
        updates.laps = null;
        updates.poolLength = null;
      } else {
        // Duration-only activities
        updates.estimatedDistance = null;
        updates.distanceUnit = null;
        updates.laps = null;
        updates.poolLength = null;
      }
      
      onEdit(editActivity.activityId, updates);
    } else {
      // Add mode: call onSave with new activity data
      const activityData = {
        name: selectedActivity,
        duration: totalDurationMinutes,
        durationSeconds: totalDurationSeconds,
        scheduledTime: formatTime(),
      };
      
      // Add sport-specific fields
      if (isSwimActivity) {
        const swimDistance = calculateSwimDistance();
        activityData.laps = laps ? parseInt(laps, 10) : null;
        activityData.poolLength = poolLength;
        // Also store calculated distance for Apple Health compatibility
        activityData.distance = swimDistance?.distance || null;
        activityData.distanceUnit = swimDistance?.unit || null;
      } else if (isDistanceActivity) {
        activityData.distance = distance ? parseFloat(distance) : null;
        activityData.distanceUnit = distanceUnit;
        activityData.laps = null;
        activityData.poolLength = null;
      } else {
        // Duration-only activities
        activityData.distance = null;
        activityData.distanceUnit = null;
        activityData.laps = null;
        activityData.poolLength = null;
      }
      
      onSave(activityData);
    }
    onClose();
  };

  const handleSkip = () => {
    if (isEditMode) {
      // In edit mode, skip just closes without saving
      onClose();
    } else {
      // In add mode, save with just the activity name, no details
      const activityData = {
        name: selectedActivity,
        duration: null,
        distance: null,
        distanceUnit: null,
        scheduledTime: null,
      };
      onSave(activityData);
      onClose();
    }
  };

  const handleBack = () => {
    if (isEditMode) {
      // In edit mode, back just closes (can't change activity type)
      onClose();
    } else {
      setStep(1);
      setSelectedActivity('');
    }
  };

  const handleDelete = () => {
    if (isEditMode && onDelete && editActivity) {
      onDelete(editActivity.activityId);
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Auto-format time inputs (number type)
  const handleHourChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setTimeHour('');
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setTimeHour(Math.min(Math.max(num, 1), 12).toString());
    }
  };

  const handleMinuteChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setTimeMinute('');
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setTimeMinute(Math.min(Math.max(num, 0), 59).toString());
    }
  };

  // Duration input handlers (number type)
  const handleDurationHoursChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setDurationHours('');
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setDurationHours(Math.min(Math.max(num, 0), 23).toString());
    }
  };

  const handleDurationMinutesChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setDurationMinutes('');
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setDurationMinutes(Math.min(Math.max(num, 0), 59).toString());
    }
  };

  const handleDurationSecondsChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setDurationSeconds('');
      return;
    }
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setDurationSeconds(Math.min(Math.max(num, 0), 59).toString());
    }
  };

  // Get title based on mode
  const getTitle = () => {
    if (isEditMode) {
      return `Edit ${selectedActivity}`;
    }
    return step === 1 ? 'Add Activity' : selectedActivity;
  };

  return (
    <div className="quick-add-modal-overlay" onClick={handleOverlayClick}>
      <div className="quick-add-modal">
        {/* Header */}
        <div className="quick-add-header">
          {step === 2 && !isEditMode && (
            <button className="quick-add-back-btn" onClick={handleBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          <div className="quick-add-title-container">
            <h2 className="quick-add-title">{getTitle()}</h2>
            {date && (
              <span className="quick-add-date">{formatDate(date)}</span>
            )}
          </div>
          <button className="quick-add-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Step 1: Activity Selection (Add mode only) */}
        {step === 1 && !isEditMode && (
          <div className="quick-add-content">
            <div className="quick-add-activity-grid">
              {ALL_ACTIVITIES.map((activity) => (
                <button
                  key={activity}
                  className="c-ripple quick-add-activity-btn"
                  onClick={() => handleActivitySelect(activity)}
                >
                  {activity}
                </button>
              ))}
              
              {/* Custom Activity */}
              {isAddingCustom ? (
                <div className="quick-add-custom-input-container">
                  <input
                    type="text"
                    value={customActivity}
                    onChange={(e) => setCustomActivity(e.target.value)}
                    onKeyDown={handleCustomActivityKeyDown}
                    onBlur={() => {
                      if (customActivity.trim()) {
                        handleCustomActivitySubmit();
                      } else {
                        setIsAddingCustom(false);
                      }
                    }}
                    placeholder="Activity name"
                    className="quick-add-custom-input"
                    autoFocus
                  />
                </div>
              ) : (
                <button
                  className="c-ripple quick-add-activity-btn quick-add-custom-btn"
                  onClick={() => setIsAddingCustom(true)}
                >
                  <span className="quick-add-plus">+</span>
                  Custom
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Activity Details */}
        {step === 2 && (
          <div className="quick-add-content">
            {/* Duration - Time input (hours:minutes) */}
            <div className="quick-add-field">
              <label className="quick-add-label">Duration</label>
              <div className="quick-add-duration-row">
                <input
                  type="number"
                  value={durationHours}
                  onChange={handleDurationHoursChange}
                  onFocus={() => setDurationHours('')}
                  placeholder="0"
                  className="quick-add-duration-input"
                  min="0"
                  max="23"
                  inputMode="numeric"
                />
                <span className="quick-add-duration-unit">hr</span>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={handleDurationMinutesChange}
                  onFocus={() => setDurationMinutes('')}
                  placeholder="00"
                  className="quick-add-duration-input"
                  min="0"
                  max="59"
                  inputMode="numeric"
                />
                <span className="quick-add-duration-unit">min</span>
                <input
                  type="number"
                  value={durationSeconds}
                  onChange={handleDurationSecondsChange}
                  onFocus={() => setDurationSeconds('')}
                  placeholder="00"
                  className="quick-add-duration-input"
                  min="0"
                  max="59"
                  inputMode="numeric"
                />
                <span className="quick-add-duration-unit">sec</span>
              </div>
            </div>

            {/* Swimming: Laps + Pool Length */}
            {isSwimActivity && (
              <>
                <div className="quick-add-field">
                  <label className="quick-add-label">Laps</label>
                  <div className="quick-add-distance-row">
                    <input
                      type="number"
                      value={laps}
                      onChange={(e) => setLaps(e.target.value)}
                      onFocus={() => setLaps('')}
                      placeholder="0"
                      className="quick-add-distance-input"
                      min="0"
                      inputMode="numeric"
                    />
                    <span className="quick-add-distance-unit">laps</span>
                  </div>
                </div>
                <div className="quick-add-field">
                  <label className="quick-add-label">Pool Length</label>
                  <div className="quick-add-pool-length-row">
                    {POOL_LENGTHS.map((pool) => (
                      <button
                        key={pool.value}
                        type="button"
                        className={`c-ripple quick-add-pool-btn ${poolLength === pool.value ? 'selected' : ''}`}
                        onClick={() => setPoolLength(pool.value)}
                      >
                        {pool.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Distance activities: miles/km - show for anything not swim and not duration-only */}
            {!isSwimActivity && !isDurationOnlyActivity && (
              <div className="quick-add-field">
                <label className="quick-add-label">Distance</label>
                <div className="quick-add-distance-row">
                  <input
                    type="number"
                    value={distance}
                    onChange={(e) => setDistance(e.target.value)}
                    onFocus={() => setDistance('')}
                    placeholder="0"
                    className="quick-add-distance-input"
                    step="0.1"
                    min="0"
                    inputMode="decimal"
                  />
                  <span className="quick-add-distance-unit">{distanceUnit}</span>
                </div>
              </div>
            )}

            {/* Duration-only activities: no distance field */}

            {/* Time of Day */}
            <div className="quick-add-field">
              <label className="quick-add-label">Time of Day</label>
              <div className="quick-add-time-row">
                <input
                  type="number"
                  value={timeHour}
                  onChange={handleHourChange}
                  onFocus={() => setTimeHour('')}
                  placeholder="12"
                  className="quick-add-time-input"
                  min="1"
                  max="12"
                  inputMode="numeric"
                />
                <span className="quick-add-time-separator">:</span>
                <input
                  type="number"
                  value={timeMinute}
                  onChange={handleMinuteChange}
                  onFocus={() => setTimeMinute('')}
                  placeholder="00"
                  className="quick-add-time-input"
                  min="0"
                  max="59"
                  inputMode="numeric"
                />
                <div className="quick-add-period-toggle">
                  <button
                    className={`c-ripple quick-add-period-btn ${timePeriod === 'AM' ? 'selected' : ''}`}
                    onClick={() => setTimePeriod('AM')}
                  >
                    AM
                  </button>
                  <button
                    className={`c-ripple quick-add-period-btn ${timePeriod === 'PM' ? 'selected' : ''}`}
                    onClick={() => setTimePeriod('PM')}
                  >
                    PM
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="quick-add-actions">
              {isEditMode && onDelete && (
                <button 
                  className="quick-add-delete-btn"
                  onClick={handleDelete}
                >
                  Delete Activity
                </button>
              )}
              <div className="quick-add-actions-right">
                <button 
                  className="c-ripple quick-add-skip-btn"
                  onClick={handleSkip}
                >
                  {isEditMode ? 'Cancel' : 'Skip'}
                </button>
                <button 
                  className="c-ripple quick-add-save-btn"
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuickAddActivityModal;
